import {
  React,
  useEffect,
  useState,
  useCallback,
  useRef,
  useLoaderData,
  useLocation,
  useClientInfo,
  axiosInstance,
  tableStyles,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Typography,
  Menu,
  TablePagination,
  // Snackbar
  // Alert,
  MoreVertIcon,
  AddIcon,
  DeleteIcon,
  EditIcon,
  VendorFormAdd,
} from "../../utils/commonImports";

const API = "/api/vendors";

const FILTERS = [
  "All Vendors",
  "Active",
  "Inactive",
  "Business",
  "Individual",
];

export default function VendorForm() {
  const { clientId } = useClientInfo();

  // Inside your VendorForm component
  const tableRef = useRef();
  const [savedScroll, setSavedScroll] = useState(0);

  // 👇 Load initial data from the route loader on first render
  const location = useLocation();
  const loaderData = useLoaderData();
  const [rows, setRows] = useState(Array.isArray(loaderData) ? loaderData : []);

  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [filter, setFilter] = useState(FILTERS[0]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchRows = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await axiosInstance.get(
        `/api/vendors?client_id=${clientId}`
      );
      setRows(res.data);
    } catch (err) {
      setSnackbar({
        open: true,
        msg: "Failed to fetch vendors.",
        severity: "error",
      });
    }
  }, [clientId]);

  useEffect(() => {
    if (!showForm && tableRef.current) {
      tableRef.current.scrollTop = savedScroll;
    }
  }, [showForm, savedScroll]);

  useEffect(() => {
    // Optionally, re-run fetch or reset state on each location change
    fetchRows();
  }, [location.key]);

  // Filtering client-side (adjust for server-filter as needed)
  const filteredRows = rows.filter((row) => {
    if (filter === "All Vendors") return true;
    if (filter === "Active") return row.vendoractivestatus === "Active";
    if (filter === "Inactive") return row.vendoractivestatus === "Inactive";
    if (filter === "Business") return row.vendortype === "Business";
    if (filter === "Individual") return row.vendortype === "Individual";
    return true;
  });

  // Pagination slicing
  const pagedRows = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Row menu handlers
  const handleRowMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };
  const handleRowMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  const handleDelete = async (row) => {
    handleRowMenuClose();
    if (!window.confirm("Are you sure you want to delete this vendor?"))
      return;

    if (!clientId) {
      setSnackbar({
        open: true,
        msg: "Client ID not available.",
        severity: "error",
      });
      return;
    }

    try {
      await axiosInstance.delete(
        `${API}/${row.vendorid}?client_id=${clientId}`
      );
      setSnackbar({
        open: true,
        msg: "Vendor deleted.",
        severity: "success",
      });
      fetchRows();
    } catch (err) {
      setSnackbar({ open: true, msg: "Delete failed.", severity: "error" });
    }
  };

  // Edit
  const handleEdit = async (row) => {
    try {
      if (tableRef.current) {
        setSavedScroll(tableRef.current.scrollTop);
      }
      // Fetch the FULL vendor object, including contacts!
      const res = await axiosInstance.get(
        `/api/vendors/${row.vendorid}?client_id=${clientId}`
      );

      console.log("Fetched vendor for edit:", res.data);

      setEditData(res.data);
      setShowForm(true);
    } catch (err) {
      setSnackbar({
        open: true,
        msg: "Failed to fetch full vendor details for edit.",
        severity: "error",
      });
    }
    handleRowMenuClose();
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        p: { xs: 0, md: 0 },
        bgcolor: "#f8fafc",
      }}
    >
      {/* Header: Filter + Add */}
      {!showForm && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            mt: 1,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="vendor-filter-label">Filter</InputLabel>
            <Select
              labelId="vendor-filter-label"
              value={filter}
              label="Filter"
              onChange={(e) => setFilter(e.target.value)}
              sx={{ fontSize: 13 }}
            >
              {FILTERS.map((f) => (
                <MenuItem key={f} value={f} sx={{ fontSize: 13 }}>
                  {f}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            sx={{
              ml: 2,
              fontSize: 13,
              textTransform: "none",
              px: 1.5,
              py: 0.5,
              backgroundColor: "#3B82F6", // blue-500 sidebar border
              "&:hover": {
                backgroundColor: "#2563eb", // blue-600 for hover, as per Tailwind
              },
              boxShadow: "none",
            }}
            onClick={() => {
              setEditData(null);
              setShowForm(true);
            }}
          >
            Add
          </Button>
        </Box>
      )}

      {/* Table view or Add/Edit form */}
      {showForm ? (
        <VendorFormAdd
          editData={editData}
          onCancel={() => setShowForm(false)}
          onSuccess={() => {
            setShowForm(false);
            setSnackbar({
              open: true,
              msg: "Contact information has been saved.",
              severity: "success",
            });
            fetchRows();
          }}
        />
      ) : (
        <>
          <Paper
            ref={tableRef}
            elevation={2}
            sx={{ width: "100%", overflowX: "auto", borderRadius: 2 }}
          >
            <Table
              size="small"
              sx={{
                width: "100%",
                minWidth: 1000,
                tableLayout: "fixed", // Fixed table layout for width control
                ...tableStyles,
              }}
            >
              <TableHead>
                <TableRow sx={{ height: 36 }}>
                  <TableCell
                    sx={{
                      px: 1,
                      py: 0.5,
                      width: 250,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Vendor Name
                  </TableCell>
                  <TableCell sx={{ px: 1, py: 0.5, width: 120 }}>
                    Type
                  </TableCell>
                  <TableCell
                    sx={{
                      px: 1,
                      py: 0.5,
                      width: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Contact Person
                  </TableCell>
                  <TableCell
                    sx={{
                      px: 1,
                      py: 0.5,
                      width: 250,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Email
                  </TableCell>
                  <TableCell sx={{ px: 1, py: 0.5, width: 120 }}>
                    Phone
                  </TableCell>
                  <TableCell sx={{ px: 1, py: 0.5, width: 80 }}>
                    Status
                  </TableCell>
                  <TableCell
                    sx={{ px: 0, py: 0.5, width: 50 }}
                    align="right"
                  ></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((row) => (
                  <TableRow key={row.vendorid} hover sx={{ height: 36 }}>
                    <TableCell
                      sx={{
                        px: 1,
                        py: 0.5,
                        width: 250,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.vendorname}
                    </TableCell>
                    <TableCell sx={{ px: 1, py: 0.5, width: 120 }}>
                      {row.vendortype}
                    </TableCell>
                    <TableCell
                      sx={{
                        px: 1,
                        py: 0.5,
                        width: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.vendorprimarycontactname}
                    </TableCell>
                    <TableCell
                      sx={{
                        px: 1,
                        py: 0.5,
                        width: 250,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.vendorprimarycontactemailaddress}
                    </TableCell>
                    <TableCell sx={{ px: 1, py: 0.5, width: 120 }}>
                      {row.vendorprimarycontactphone}
                    </TableCell>
                    <TableCell sx={{ px: 1, py: 0.5, width: 80 }}>
                      {row.vendoractivestatus}
                    </TableCell>
                    <TableCell sx={{ px: 0, py: 0.5, width: 50 }} align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleRowMenuOpen(e, row)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {pagedRows.length === 0 && (
                  <TableRow sx={{ height: 36 }}>
                    <TableCell colSpan={7}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                      >
                        No vendors to display.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={filteredRows.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(+e.target.value);
                setPage(0);
              }}
              labelRowsPerPage="Rows per page"
              labelDisplayedRows={({ from, to, count }) =>
                `Showing ${from}-${to} of ${count}`
              }
              sx={{
                ".MuiTablePagination-toolbar": { minHeight: 38, height: 38 },
                ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                  { fontSize: 12 },
              }}
            />
          </Paper>
          {/* Row Actions: Edit/Delete menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleRowMenuClose}
            PaperProps={{ sx: { minWidth: 120 } }}
          >
            <MenuItem onClick={() => handleEdit(menuRow)} sx={{ fontSize: 13 }}>
              <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
            </MenuItem>
            <MenuItem
              onClick={() => handleDelete(menuRow)}
              sx={{ fontSize: 13, color: "error.main" }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
            </MenuItem>
          </Menu>
        </>
      )}
      {/* <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }} // <-- Add this line
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.msg}
        </Alert>
      </Snackbar> */}
    </Box>
  );
}
